// =================================================================
// 1. GLOBAL SETUP & CORE FUNCTIONS
// =================================================================
const defaultDiscountPercentage = window.discountPercentage || 0;
let currentDiscountPercentage = window.discountPercentage || 0;

function updatePrices(discountPercentage) {
  function calculateDiscountedPrice(originalPrice, discountPercentage) {
    return originalPrice * (1 - discountPercentage / 100);
  }

  function formatPrice(price) {
    const formattedPrice = '$' + Math.abs(price).toFixed(2);
    return formattedPrice.endsWith('.00') ? formattedPrice.slice(0, -3) : formattedPrice;
  }

  function parsePrice(priceString) {
    const matches = priceString.match(/(\d+(?:\.\d{1,2})?)/g);
    if (matches && matches.length > 0) {
      const prices = matches.map((match) => parseFloat(match));
      return Math.max(...prices);
    }
    return NaN;
  }

  requestAnimationFrame(() => {
    // --- Standard Theme Prices ---
    const priceElements = document.querySelectorAll('.price');
    priceElements.forEach((priceContainer) => {
      if (priceContainer.closest('[data-exclude-strikethrough="true"]')) return;

      const targetWrapper = priceContainer.querySelector('ins') || priceContainer;
      const amountSpan = targetWrapper.querySelector('.amount');
      if (!amountSpan) return;

      const hasTieredPrice = amountSpan.querySelector('.tiered-sale-price');

      // Skip if the DOM is intact AND the tier hasn't changed
      if (hasTieredPrice && amountSpan.dataset.discountApplied === String(discountPercentage)) {
        return;
      }

      const existingStrike = amountSpan.querySelector('s');
      let originalPriceText = existingStrike ? existingStrike.textContent.trim() : amountSpan.textContent.replace(/Regular price:|Sale price:/g, '').trim();

      const originalPrice = parsePrice(originalPriceText);
      if (isNaN(originalPrice)) return;

      const hasFrom = amountSpan.querySelector('.from') !== null;
      const fromHTML = hasFrom ? '<span class="from" aria-hidden="true">From</span> ' : '';

      amountSpan.dataset.discountApplied = discountPercentage;

      if (discountPercentage <= 0) {
        amountSpan.innerHTML = `${fromHTML}${formatPrice(originalPrice)}`;
        return;
      }

      const discountedPrice = calculateDiscountedPrice(originalPrice, discountPercentage);
      amountSpan.innerHTML = `
        ${fromHTML}
        <span class="visually-hidden">Regular price: </span>
        <s aria-hidden="true">${formatPrice(originalPrice)}</s> 
        &nbsp;
        <span class="visually-hidden">Sale price: </span>
        <span class="tiered-sale-price">${formatPrice(discountedPrice)}</span>
      `;
    });

    // --- Upsell Module Prices ---
    const upsellElements = document.querySelectorAll('.upsell-module-metaobject--item-price');
    upsellElements.forEach((upsellContainer) => {
      if (upsellContainer.closest('[data-exclude-strikethrough="true"]')) return;
      
      const hasTieredPrice = upsellContainer.querySelector('.tiered-sale-price');

      if (hasTieredPrice && upsellContainer.dataset.discountApplied === String(discountPercentage)) {
        return;
      }

      const existingStrike = upsellContainer.querySelector('s');
      let originalPriceText = existingStrike ? existingStrike.textContent.trim() : upsellContainer.textContent.replace(/Regular price:|Sale price:/g, '').trim();

      const originalPrice = parsePrice(originalPriceText);
      if (isNaN(originalPrice)) return;

      upsellContainer.dataset.discountApplied = discountPercentage;

      if (discountPercentage <= 0) {
        upsellContainer.innerHTML = formatPrice(originalPrice);
        return;
      }

      const discountedPrice = calculateDiscountedPrice(originalPrice, discountPercentage);
      upsellContainer.innerHTML = `
        <span class="visually-hidden">Regular price: </span>
        <s aria-hidden="true">${formatPrice(originalPrice)}</s> 
        &nbsp;
        <span class="visually-hidden">Sale price: </span>
        <span class="tiered-sale-price">${formatPrice(discountedPrice)}</span>
      `;
    });
  });
}

window.applyStrikethroughPrices = updatePrices;

// =================================================================
// 2. GLOBAL WATCHDOG (Catches variants, bundles, and slow loads)
// =================================================================
const priceWatchdog = new MutationObserver((mutations) => {
  let shouldUpdate = false;

  for (const mutation of mutations) {
    // SCENARIO A: Brand new HTML injected (Variant changes)
    if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) { 
          if (node.matches('.price, .upsell-module-metaobject--item-price') || 
              node.querySelector('.price, .upsell-module-metaobject--item-price')) {
            shouldUpdate = true;
            break;
          }
        }
      }
    }

    // SCENARIO B: Text swapped inside existing element (Bundle changes)
    if (!shouldUpdate && (mutation.type === 'childList' || mutation.type === 'characterData')) {
      const targetElement = mutation.target.nodeType === 1 ? mutation.target : mutation.target.parentElement;
      const parentPrice = targetElement ? targetElement.closest('.price, .upsell-module-metaobject--item-price') : null;

      if (parentPrice && !parentPrice.querySelector('.tiered-sale-price')) {
        shouldUpdate = true;
      }
    }

    if (shouldUpdate) break;
  }

  if (shouldUpdate) {
    clearTimeout(window.strikethroughDebounce);
    window.strikethroughDebounce = setTimeout(() => {
      requestAnimationFrame(() => updatePrices(currentDiscountPercentage));
    }, 50);
  }
});

// =================================================================
// 3. BUNDLE CLICK FAILSAFE & STICKY CART SYNC
// =================================================================
document.addEventListener('click', (event) => {
  const bundleBtn = event.target.closest('.bundle-options__item');
  
  if (bundleBtn) {
    const rawPriceCents = bundleBtn.getAttribute('data-bundle-price');
    const stickyAmount = document.querySelector('.product-add-to-cart-sticky--price .price .amount');
    
    if (rawPriceCents && stickyAmount) {
      const basePrice = parseInt(rawPriceCents, 10) / 100;
      const formattedBasePrice = '$' + basePrice.toFixed(2).replace(/\.00$/, ''); 
      
      const hasFrom = stickyAmount.querySelector('.from') !== null;
      const fromHTML = hasFrom ? '<span class="from" aria-hidden="true">From</span> ' : '';
      
      stickyAmount.innerHTML = fromHTML + formattedBasePrice;
      delete stickyAmount.dataset.discountApplied;
      
      const bundleName = bundleBtn.getAttribute('data-bundle-name');
      const stickyTitle = document.querySelector('.product-add-to-cart-sticky--info');
      if (bundleName && stickyTitle) {
        stickyTitle.textContent = bundleName;
      }
    }

    setTimeout(() => { requestAnimationFrame(() => updatePrices(currentDiscountPercentage)); }, 50);
    setTimeout(() => { requestAnimationFrame(() => updatePrices(currentDiscountPercentage)); }, 500);
  }
});

// =================================================================
// 4. IMMEDIATE EXECUTION (Eliminates load lag)
// =================================================================
document.addEventListener('DOMContentLoaded', () => {
  updatePrices(currentDiscountPercentage);
  
  // Attach observer to body to catch everything
  priceWatchdog.observe(document.body, { 
    childList: true, 
    characterData: true, 
    subtree: true 
  });
});

// =================================================================
// 5. REBUY DYNAMIC CART LOGIC
// =================================================================
document.addEventListener('rebuy:smartcart.ready', () => {
  let isInitialized = false;

  function handleCartChange() {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        let hasItems = cart.item_count > 0;
        let newDiscountPercentage = defaultDiscountPercentage;

        if (cart.cart_level_discount_applications?.length > 0) {
          cart.cart_level_discount_applications.forEach((discountApplication) => {
            if (discountApplication.value !== "") { 
              newDiscountPercentage = parseFloat(discountApplication.value);
            }
          });
        }

        // Trigger re-render if tier changed
        if (newDiscountPercentage !== currentDiscountPercentage) {
          currentDiscountPercentage = newDiscountPercentage;
          window.discountPercentage = newDiscountPercentage;
          window.strikethroughDiscountMultiplier = 1 - newDiscountPercentage / 100;
          
          updatePrices(newDiscountPercentage);
        }
        
        isInitialized = true;
      })
      .catch(() => {
        if (!isInitialized) {
          updatePrices(currentDiscountPercentage);
          isInitialized = true;
        }
      });
  }

  handleCartChange();
  document.addEventListener('rebuy:cart.change', handleCartChange);

  document.addEventListener("rebuy:smartcart.line-item-removed", () => {
    setTimeout(handleCartChange, 100); 
  });
});